import { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import pxFromCm from "../util/pxFromCm";
import { FlyerType } from "../FlyerTypePicker";
import drawBoycottHeader from "./drawBoycottHeader";
import { BoycottHeaderProps, EuropeHeaderProps } from "../editor/headerTypes";
import EuropeHeaderPreview from "./EuropeHeaderPreview";
import SubheaderPreview from "./SubheaderPreview";
import FooterPreview from "./FooterPreview";
import { HeaderProps } from "../FlyerPreview";
import { FooterProps } from "../editor/FooterEditor";
import { AlternativesVP } from "../editor/AlternativesEditor";
import { useFlyerConfig } from "../util/useFlyerConfig";


interface FlyerContentChildCanvasAndTableProps {
  headerProps: HeaderProps;
  subheader: string;
  footerProps: FooterProps;
  textColor: string;
  alternatives: AlternativesVP;
}


export default function FlyerContentChildCanvasAndTable(
  props: FlyerContentChildCanvasAndTableProps)
{
  const [headerHeight, setHeaderHeight] = useState(-1);
  const [fontSize, setFontSize] = useState(50);
  const [maskImg, setMaskImg] = useState<HTMLImageElement | null>(null);  
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [recursion, setRecursion] = useState(0);
  const alternativesRef = useRef(null);
  const footerRef = useRef(null);
  const footnoteRef = useRef(null);

  const { flyerType } = useFlyerConfig();
  const { headerProps, textColor, subheader,
    alternatives, footerProps,
  } = props;

  const margin = pxFromCm(1.75)
  const hasSubheader = subheader.length > 0;
  useEffect(() => {
    const img = new Image();
    img.src = "/mask.png"; // Replace with your grayscale mask image
    img.onload = () => setMaskImg(img);
  }, []);
    
  const drawFlyer = () => {
    if (!headerProps) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas || !maskImg) return;
  
    if (flyerType === FlyerType.BOYCOTT) {
      setHeaderHeight(drawBoycottHeader(
        canvas,
        margin,
        headerProps as BoycottHeaderProps,
        maskImg,
        hasSubheader
      ))
    } else {
      setHeaderHeight(0);
    }
  };

  useEffect(() => {
    drawFlyer();
  }, [maskImg, headerProps, flyerType, hasSubheader]);

  useEffect(() => {
    setRecursion(0);
  }, [footerProps, alternatives, subheader, headerHeight])

  useLayoutEffect(() => {
    if (footerRef && footerRef.current
      && alternativesRef && alternativesRef.current
      && headerHeight >= 0
      && recursion < 20
    ) {
      const alternativesRect = alternativesRef.current.getBoundingClientRect();
      const footerRect = footerRef.current.getBoundingClientRect();
      const alternativesToFooter = alternativesRect.bottom - footerRect.top;
      var extraFootnoteHeight = 0
      if (footerProps && !footerProps.imageUrl && footnoteRef && footnoteRef.current) {
        const rect = footnoteRef.current.getBoundingClientRect();
        extraFootnoteHeight = rect.bottom - alternativesRect.bottom + 10;
      }
      const overshoot = alternativesToFooter + extraFootnoteHeight 
      if (overshoot > 0) {
        if (fontSize > 30) {
          setFontSize(fontSize - 3);
          setRecursion(recursion + 1);
        }
      } else if (overshoot < -75) {
        if (fontSize < 55) {
          setFontSize(fontSize + 3);
          setRecursion(recursion + 1);
        }
      }
    }
  }, [headerHeight, footerProps, alternatives, subheader, fontSize])

  const hasAlternatives = alternatives && alternatives.categories && alternatives.categories.length > 0

  return (<>
    <canvas ref={canvasRef} ></canvas>
    {<FlyerContentChild style={{
      color: textColor
    }}>
      <div style={{
        height: `${headerHeight}px`,
        marginLeft: `${margin}px`,
        marginRight: `${margin}px`,
      }} />
      {(flyerType == "europe") && <EuropeHeaderPreview
        headerProps={headerProps as EuropeHeaderProps}
        reduceHeight={hasSubheader}
        margin={margin} />}
      {hasSubheader && <SubheaderPreview margin={margin} text={subheader}/>}
      {hasAlternatives && <div ref={alternativesRef} style={{ color: textColor, marginLeft: `${margin}px`,
        marginRight: `${margin}px`, fontSize: `${fontSize}px` }}>
        <div className="row" style={{ borderBottom: `5px solid ${textColor}`}}>
          <div className="cell-category" ></div>
          <div className="cell-avoid bold" >{alternatives.avoidHeader}</div>
          <div className="cell-consider bold" >{alternatives.considerHeader}</div>
        </div>
        {alternatives.categories.map((category, index) => (
          <div className="row" key={index} style={
            index < alternatives.categories.length // disabled
              ? { borderBottom: `5px solid ${textColor}`}
              : {}
          }>
            <div className="cell-category">{category.name}</div>
            <div className="cell-avoid">{category.avoid.map((p) => p.name).join(", ")}</div>
            <div className="cell-consider">{category.consider.map((p) => p.name).join(", ")}</div>
          </div>
        ))}
      </div>}
      {alternatives && alternatives.footnotes.length > 0 && <FootnoteContainer
        ref={footnoteRef}
        style={{ color: textColor, marginLeft: `${margin}px`,
          fontSize: fontSize
        }}
      >
        {alternatives.footnotes.map((f) => (
          <Footnote key={f}>* {f}</Footnote>
        ))}
      </FootnoteContainer>}
      {<FooterPreview ref={footerRef} footerProps={footerProps} margin={margin} textColor={textColor} />}
    </FlyerContentChild>}
  </>);
}

const FlyerContentChild = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  font-size: 45px;
`;

const FootnoteContainer = styled.div`
  width: 100%;
  margin-top: calc(5 * 10px); 
  display: flex;
  align-items: start; 
`;

const Footnote = styled.div``;

